import type { TimeOffBlock } from "@/lib/api";
import { GOAL, type TimeOffSummary, blockLabel, daysOf, formatDays } from "@/lib/timeoff";
import { Box, Text } from "@okshaun/components";
import { css } from "@styled-system/css";
import { useState } from "react";
import { pressableRaw } from "./pressable";
import { RailSection } from "./rail-section";

interface TimeOffRailProps {
  summary: TimeOffSummary | null;
  loading: boolean;
  error: string | null;
}

const toggle = css(pressableRaw, {
  px: "8",
  py: "2",
  borderRadius: "4",
  ml: "auto",
  flexShrink: 0,
});

const TONE = {
  under: css({ bg: "text.accent.purple" }),
  onTrack: css({ bg: "domain.personal" }),
  over: css({ bg: "text.warning" }),
} as const;

/** Bar from 0 to the high goal, with a mark at the low goal. */
function GoalBar({ used }: { used: number }) {
  const max = GOAL.high;
  const pct = Math.min(100, (used / max) * 100);
  const lowPct = (GOAL.low / max) * 100;
  const tone = used > GOAL.high ? TONE.over : used >= GOAL.low ? TONE.onTrack : TONE.under;
  return (
    <Box position="relative" h="6" bg="surface.sunken" borderRadius="999" overflow="hidden">
      <Box
        position="absolute"
        top="0"
        left="0"
        bottom="0"
        className={tone}
        style={{ width: `${pct}%` }}
      />
      <Box
        position="absolute"
        top="0"
        bottom="0"
        w="1"
        bg="border.bold"
        style={{ left: `${lowPct}%` }}
      />
    </Box>
  );
}

function BlockRow({ block }: { block: TimeOffBlock }) {
  return (
    <Box display="flex" alignItems="baseline" gap="8" px="12" py="3">
      <Text textStyle="mono.xs" color="text.subtle" minW="[96px]">
        {blockLabel(block)}
      </Text>
      <Text textStyle="mono.xs" color="text">
        {block.hours == null ? "? hrs" : `${formatDays(daysOf(block))} d`}
      </Text>
      <Text textStyle="mono.xs" color="text.subtlest" ml="auto">
        {block.hours == null ? "no hours in Gusto" : `${block.hours} hrs`}
      </Text>
    </Box>
  );
}

export function TimeOffRail({ summary, loading, error }: TimeOffRailProps) {
  const [open, setOpen] = useState(false);
  const meta = summary
    ? `${formatDays(summary.used)} of ${GOAL.low}–${GOAL.high} · last 12 months`
    : loading
      ? "loading…"
      : undefined;
  return (
    <RailSection title="Time off" meta={meta}>
      {error && (
        <Text textStyle="mono.sm" color="text.danger" p="8">
          time off: {error}
        </Text>
      )}
      {summary && (
        <Box display="flex" flexDir="column" gap="8" px="12" py="8">
          <GoalBar used={summary.used} />
          <Box display="flex" alignItems="center" gap="12">
            <Text textStyle="mono.xs" color="text.subtle" whiteSpace="nowrap">
              {formatDays(Math.max(0, GOAL.low - summary.used))} to {GOAL.low} ·{" "}
              {formatDays(Math.max(0, GOAL.high - summary.used))} to {GOAL.high}
            </Text>
            <button
              type="button"
              aria-expanded={open}
              className={toggle}
              onClick={() => setOpen((v) => !v)}
            >
              {open ? "hide" : `${summary.blocks.length + summary.upcoming.length} blocks`}
            </button>
          </Box>
          {(summary.booked > 0 || summary.unknown > 0) && (
            <Box display="flex" gap="12" flexWrap="wrap">
              {summary.booked > 0 && (
                <Text textStyle="mono.xs" color="text.accent.purple">
                  +{formatDays(summary.booked)} booked ahead
                </Text>
              )}
              {summary.unknown > 0 && (
                <Text textStyle="mono.xs" color="text.warning">
                  {summary.unknown} block{summary.unknown === 1 ? "" : "s"} without hours
                </Text>
              )}
            </Box>
          )}
          {open && (
            <Box display="flex" flexDir="column" mx="-12" borderTop="default" pt="6">
              {summary.upcoming.map((b) => (
                <BlockRow key={b.id} block={b} />
              ))}
              {summary.upcoming.length > 0 && summary.blocks.length > 0 && (
                <Box h="1" bg="border" my="4" />
              )}
              {summary.blocks.map((b) => (
                <BlockRow key={b.id} block={b} />
              ))}
            </Box>
          )}
        </Box>
      )}
    </RailSection>
  );
}
